/** Training, test recordings and clip playback — shared by the device pages. */
import { useEffect, useState } from "react";

import { api, usePoll } from "../api";
import type { Device, Recording, Training } from "../api";
import { fmtTimeSec } from "../format";

const STEP_TEXT: Record<string, string> = {
  requested: "Asking the device to listen…",
  capturing: "Recording — the device keeps the last 10 seconds and the next 20…",
  analysing: "Picking out the barks…",
};

export function TrainingPanel({ id, onDone }: { id: string; onDone: () => void }) {
  // Poll only until there is a result: every poll re-signs the clip URL, and
  // an audio element whose src keeps changing restarts — playback stopped
  // after a second.
  const [settled, setSettled] = useState(false);
  const { data: t, error } = usePoll<Training>(`/api/training/${id}`, settled ? undefined : 2000);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (t && (t.status === "ready" || t.status === "failed" || t.status === "accepted")) setSettled(true);
  }, [t]);

  if (error) return <p className="error">{error}</p>;
  if (!t) return <div className="panel panel-hero"><div className="spinner" /></div>;

  async function accept() {
    setBusy(true);
    await api.post(`/api/training/${id}/accept`);
    onDone();
  }

  const working = t.status in STEP_TEXT;
  return (
    <div className="panel panel-hero panel-col">
      <h3>{t.kind === "positive" ? "Learning the sound" : "Learning what to ignore"}</h3>
      {working && (
        <p className="status"><span className="spinner inline" /> {STEP_TEXT[t.status]}</p>
      )}
      {t.status === "failed" && <p className="error">{t.error ?? "That didn't work."}</p>}
      {t.status === "ready" && t.kind === "positive" && t.summary && (
        <p>
          Found <strong>{t.summary.barks} barks</strong>, pitch{" "}
          <strong>{t.summary.freq_hz?.[0]}–{t.summary.freq_hz?.[1]} Hz</strong>, level{" "}
          <strong>{t.summary.level_dbfs?.[0]} to {t.summary.level_dbfs?.[1]} dBFS</strong>.
        </p>
      )}
      {t.status === "ready" && t.kind === "negative" && (
        <p>Found {t.segments.length} sound(s) to ignore.</p>
      )}
      {t.clip_url && (
        <Clip url={t.clip_url} durationMs={30_000} markersMs={t.segments.map((s) => s.start_ms)} />
      )}
      <div className="actions">
        {t.status === "ready" && (
          <button className="primary" disabled={busy} onClick={accept}>
            {t.kind === "positive" ? "Use this" : "Ignore these"}
          </button>
        )}
        {(t.status === "ready" || t.status === "failed") && (
          <button onClick={onDone}>{t.status === "failed" ? "Back" : "Try again"}</button>
        )}
      </div>
    </div>
  );
}

export function QuietGap({ device, onSaved }: { device: Device; onSaved: () => void }) {
  const [value, setValue] = useState(device.profile!.quiet_gap_min);
  return (
    <div className="panel panel-col">
      <h3>When does one incident end?</h3>
      <p className="muted">
        After <input type="number" min={1} max={240} value={value} className="tiny"
                     onChange={(e) => setValue(Number(e.target.value))} /> minutes without a bark,
        the next bark starts a new incident.
      </p>
      {value !== device.profile!.quiet_gap_min && (
        <button onClick={async () => {
          await api.patch(`/api/devices/${device.id}/profile`, { quiet_gap_min: value });
          onSaved();
        }}>Save</button>
      )}
    </div>
  );
}

/** Recent test recordings with levels — newest first. */
export function RecordingList({ items }: { items: Recording[] }) {
  if (!items.length) return <p className="muted small">No test recordings yet.</p>;
  return (
    <ul className="recordings">
      {items.map((r) => (
        <li key={r.id}>
          <div className="small muted">
            {fmtTimeSec(r.created_at)}
            {r.levels && ` · background ${r.levels.background_dbfs} dBFS · loudest ${r.levels.peak_dbfs} dBFS`}
          </div>
          {r.clip_url ? (
            <StableAudio url={r.clip_url} />
          ) : (
            <span className="small muted">{r.status === "failed" ? "Failed" : "Not uploaded yet"}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function RecordingProgress({ id, onDone }: { id: string; onDone: () => void }) {
  const { data: t } = usePoll<Training>(`/api/training/${id}`, 1000);
  useEffect(() => {
    if (t && (t.status === "ready" || t.status === "failed")) onDone();
  }, [t, onDone]);
  const text =
    !t || t.status === "requested"
      ? "Asking the device to record…"
      : t.status === "capturing"
        ? "Recording 10 seconds, then uploading…"
        : "Finishing…";
  return <p className="status"><span className="spinner inline" /> {text}</p>;
}

/** An audio player whose source stays put when the page re-fetches and gets
 *  a newly signed URL for the same clip (which would restart playback). */
export function StableAudio({ url }: { url: string }) {
  const [src] = useState(url);
  return <audio controls preload="none" src={src} />;
}

/** An audio clip with tick marks where barks were found. */
export function Clip({ url, durationMs, markersMs }: { url: string; durationMs: number; markersMs: number[] }) {
  // Keep the first URL. Pages refresh their data on a timer and each refresh
  // brings a newly signed URL for the same clip; swapping it into the audio
  // element would restart (and so stop) playback.
  const [src] = useState(url);
  return (
    <div className="clip">
      <div className="markers" aria-hidden>
        {markersMs.map((m, i) => (
          <span key={i} style={{ left: `${Math.min(100, (m / durationMs) * 100)}%` }} />
        ))}
      </div>
      <audio controls preload="none" src={src} />
    </div>
  );
}
